import React from "react";
import dayjs from "dayjs";
import { DataItem } from "../definitions/internationalTypes";

interface InternationalDataCardProps {
  item: DataItem;
  onView: (item: DataItem) => void;
  className?: string;
}

const InternationalDataCard: React.FC<InternationalDataCardProps> = ({
  item,
  onView,
  className = "",
}) => {
  const formattedDate = dayjs(item.created_at).format("DD MMMM YYYY");

  const handleCardClick = () => {
    onView(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onView(item);
    }
  };

  return (
    <div
      className={`card bg-base-100 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer group border border-base-300 ${className}`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View ${item.judul}`}
    >
      <div className="card-body p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="card-title text-lg group-hover:text-primary transition-colors duration-200 line-clamp-2">
              {item.judul}
            </h3>
            <p className="text-sm text-base-content/70 mt-1">{item.kategori}</p>
          </div>

          {/* View Icon */}
          <div className="ml-3 flex-shrink-0">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-200">
              <svg
                className="w-4 h-4 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Institution/Country */}
        <div className="mb-4">
          <div className="badge badge-outline gap-x-1">
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {item.lembaga}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-sm text-base-content/70">
          <div className="flex items-center gap-x-1">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formattedDate}
          </div>

          <div className="flex items-center gap-x-1">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {item.size}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternationalDataCard;
